import { Link } from 'expo-router';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { Bell, Download, Smartphone } from 'lucide-react-native';

import { Button } from '@/src/components/Button';
import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';

import { usePwaInstall } from './use-pwa-install';

type PwaInstallCardProps = {
  compact?: boolean;
  dismissible?: boolean;
  ignoreDismissed?: boolean;
};

export function PwaInstallCard({
  compact = false,
  dismissible = true,
  ignoreDismissed = false,
}: PwaInstallCardProps) {
  const install = usePwaInstall();

  if (
    !install.isWeb ||
    install.isStandalone ||
    (install.dismissed && !ignoreDismissed)
  ) {
    return null;
  }

  const title = install.canPromptInstall
    ? 'Install Frienzzi'
    : 'Add Frienzzi to your home screen';
  const body = install.canPromptInstall
    ? 'Install the app for a faster home-screen experience and browser push notifications after setup.'
    : install.isIosSafari
      ? 'Tap Share in Safari, then Add to Home Screen. After opening the installed app, you can enable push notifications in Settings.'
      : 'Use your browser menu to install Frienzzi. After opening the installed app, you can enable push notifications in Settings.';

  return (
    <View style={[styles.card, compact ? styles.cardCompact : null]}>
      <View style={styles.iconShell}>
        {install.canPromptInstall ? (
          <Download color={colors.primary} size={24} strokeWidth={2.5} />
        ) : (
          <Smartphone color={colors.primary} size={24} strokeWidth={2.5} />
        )}
      </View>
      <View style={styles.copy}>
        <Text style={styles.title}>{title}</Text>
        <Text style={styles.body}>{body}</Text>
        <View style={styles.pushHint}>
          <Bell color={colors.secondary} size={16} strokeWidth={2.5} />
          <Text style={styles.pushHintText}>
            Push permission is only requested when you enable it.
          </Text>
        </View>
        <View style={styles.actions}>
          {install.canPromptInstall ? (
            <Button
              title="Install app"
              onPress={() => {
                void install.promptInstall();
              }}
              style={styles.actionButton}
            />
          ) : null}
          <Link href="/install" asChild>
            <Pressable accessibilityRole="link" style={styles.dismissButton}>
              <Text style={styles.dismissText}>Install guide</Text>
            </Pressable>
          </Link>
          {dismissible ? (
            <>
              <Pressable
                accessibilityRole="button"
                onPress={install.markInstallDone}
                style={styles.dismissButton}
              >
                <Text style={styles.dismissText}>Already done</Text>
              </Pressable>
              <Pressable
                accessibilityRole="button"
                onPress={install.snoozeInstallCard}
                style={styles.dismissButton}
              >
                <Text style={styles.dismissText}>Do this later</Text>
              </Pressable>
            </>
          ) : null}
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  actions: {
    alignItems: 'center',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  actionButton: {
    flexGrow: 1,
  },
  body: {
    color: colors.mutedText,
    fontSize: 14,
    lineHeight: 20,
  },
  card: {
    alignItems: 'flex-start',
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    flexDirection: 'row',
    gap: spacing.md,
    padding: spacing.lg,
  },
  cardCompact: {
    padding: spacing.md,
  },
  copy: {
    flex: 1,
    gap: spacing.sm,
  },
  dismissButton: {
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.sm,
  },
  dismissText: {
    color: colors.secondary,
    fontSize: 14,
    fontWeight: '900',
  },
  iconShell: {
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 999,
    borderWidth: 1,
    height: 44,
    justifyContent: 'center',
    width: 44,
  },
  pushHint: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.xs,
  },
  pushHintText: {
    color: colors.text,
    flex: 1,
    fontSize: 13,
    fontWeight: '700',
    lineHeight: 18,
  },
  title: {
    color: colors.text,
    fontSize: 17,
    fontWeight: '900',
  },
});
